import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

import { AuthProvider } from "@/lib/auth";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Coverage from "@/pages/Coverage";
import Carriers from "@/pages/Carriers";
import HowItWorks from "@/pages/HowItWorks";
import JarvisPage from "@/pages/Jarvis";
import Book from "@/pages/Book";
import Join from "@/pages/Join";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AuthVerify from "@/pages/AuthVerify";
import AuthGoogleCallback from "@/pages/AuthGoogleCallback";
import Login from "@/pages/Login";
import AccountPassword from "@/pages/AccountPassword";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";
import CoveragePage from "@/pages/CoveragePage";
import NotFound from "@/pages/NotFound";

import AdminShell from "@/components/admin/AdminShell";
import RequireRole from "@/components/admin/RequireRole";
import PasswordMustChangeGate from "@/components/PasswordMustChangeGate";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminVipSplitScreen from "@/pages/admin/AdminVipSplitScreen";
import AdminConversations from "@/pages/admin/AdminConversations";
import AdminCompliance from "@/pages/admin/AdminCompliance";
import AdminBooking from "@/pages/admin/AdminBooking";
import AdminRecruiting from "@/pages/admin/AdminRecruiting";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminTeamMember from "@/pages/admin/AdminTeamMember";
import AdminPolicies from "@/pages/admin/AdminPolicies";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminBlogEditor from "@/pages/admin/AdminBlogEditor";
import AdminBlogImport from "@/pages/admin/AdminBlogImport";
import MyTeamTree from "@/pages/admin/MyTeamTree";
import AdminPoliciesByAgent from "@/pages/admin/AdminPoliciesByAgent";
import AdminFunnel from "@/pages/admin/AdminFunnel";
import AdminActivityLog from "@/pages/admin/AdminActivityLog";
// AdminScorecards route was removed in the Phase 9 course-correction (all
// projected-commission UI stripped from the app).
import MyPipeline from "@/pages/MyPipeline";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import VerifyIdPage from "@/pages/VerifyIdPage";
import NotAuthorized from "@/pages/admin/NotAuthorized";
import { Toaster } from "@/components/ui/sonner";

function ScrollManager() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const el = document.querySelector(hash);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }
        }
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname, hash]);
    return null;
}

export default function App() {
    return (
        <div className="App" data-testid="app-root">
            <BrowserRouter>
                <AuthProvider>
                    <ScrollManager />
                    <PasswordMustChangeGate>
                        <Routes>
                        {/* Public shell with header/footer */}
                        <Route element={<Layout />}>
                            <Route index element={<Home />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/coverage" element={<Coverage />} />
                            <Route path="/coverage/:slug" element={<CoveragePage />} />
                            <Route path="/carriers" element={<Carriers />} />
                            <Route path="/how-it-works" element={<HowItWorks />} />
                            <Route path="/jarvis" element={<JarvisPage />} />
                            <Route path="/book" element={<Book />} />
                            <Route path="/join" element={<Join />} />
                            <Route path="/faq" element={<FAQ />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/blog" element={<BlogIndex />} />
                            <Route path="/blog/:slug" element={<BlogPost />} />
                            <Route path="/auth/verify" element={<AuthVerify />} />
                            <Route path="*" element={<NotFound />} />
                        </Route>

                        {/* Standalone auth page */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/auth/google/callback" element={<AuthGoogleCallback />} />
                        <Route path="/account/password" element={<AccountPassword />} />
                        <Route path="/verify-id/:token" element={<VerifyIdPage />} />
                        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />

                        {/* Agent / staff personal dashboard */}
                        <Route
                            path="/my"
                            element={
                                <RequireRole roles={["master_admin", "admin", "agent"]}>
                                    <MyPipeline />
                                </RequireRole>
                            }
                        />

                        {/* Admin shell + protected routes */}
                        <Route path="/admin/not-authorized" element={<NotAuthorized />} />
                        <Route
                            path="/admin"
                            element={
                                <RequireRole roles={["master_admin", "admin", "agent"]}>
                                    <AdminShell />
                                </RequireRole>
                            }
                        >
                            <Route index element={<AdminDashboard />} />
                            <Route path="leads" element={<AdminLeads />} />
                            <Route path="leads/:id/vip" element={<AdminVipSplitScreen />} />
                            <Route path="conversations" element={<AdminConversations />} />
                            <Route path="compliance" element={<AdminCompliance />} />
                            <Route path="booking" element={<AdminBooking />} />
                            <Route path="policies" element={<AdminPolicies />} />
                            <Route
                                path="reports/policies-by-agent"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <AdminPoliciesByAgent />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="reports/funnel"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <AdminFunnel />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="activity-log"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <AdminActivityLog />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="recruiting"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <AdminRecruiting />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="team"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <AdminTeam />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="team/:id"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <AdminTeamMember />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="users"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <AdminUsers />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="blog"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <AdminBlog />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="blog/new"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <AdminBlogEditor />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="blog/import"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <AdminBlogImport />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="blog/:id/edit"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <AdminBlogEditor />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="my-team"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <MyTeamTree />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="settings"
                                element={
                                    <RequireRole roles={["master_admin", "admin"]}>
                                        <AdminSettings />
                                    </RequireRole>
                                }
                            />
                        </Route>
                    </Routes>
                    </PasswordMustChangeGate>
                    <Toaster position="bottom-right" richColors closeButton />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}
